import React, { useState, useEffect } from 'react';
import { Calendar, Battery, Heart, Target, Send } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${API_BASE}/api`;

const DailyCheckin = ({ onComplete, onStatsUpdate }) => {
  const [goals, setGoals] = useState([]);
  const [checkinData, setCheckinData] = useState({
    energy_level: 5,
    mood: '',
    notes: '',
    goals_progress: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const moods = [
    { value: 'energized', label: 'ENERGIZED', emoji: '⚡', color: 'text-neon-green' },
    { value: 'focused', label: 'FOCUSED', emoji: '🎯', color: 'text-electric-blue' },
    { value: 'motivated', label: 'MOTIVATED', emoji: '🚀', color: 'text-yellow-400' },
    { value: 'calm', label: 'CALM', emoji: '🧘', color: 'text-blue-400' },
    { value: 'stressed', label: 'STRESSED', emoji: '😤', color: 'text-orange-400' },
    { value: 'tired', label: 'TIRED', emoji: '😴', color: 'text-gray-400' }
  ];

  useEffect(() => {
    loadActiveGoals();
  }, []);

  const loadActiveGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      const activeGoals = response.data.filter(goal => goal.status === 'active');
      setGoals(activeGoals);
      
      // Initialize goals progress
      setCheckinData(prev => ({
        ...prev,
        goals_progress: activeGoals.map(goal => ({
          goal_id: goal.id,
          progress_made: 0,
          notes: ''
        }))
      }));
    } catch (error) {
      console.error('Failed to load goals:', error);
    }
  };

  const updateGoalProgress = (goalId, field, value) => {
    setCheckinData(prev => ({
      ...prev,
      goals_progress: prev.goals_progress.map(progress =>
        progress.goal_id === goalId
          ? { ...progress, [field]: value }
          : progress
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!checkinData.mood) {
      alert('Please select your mood for today!');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Filter out goals with no progress
      const goalsWithProgress = checkinData.goals_progress.filter(
        progress => progress.progress_made > 0
      );

      await axios.post(`${API}/checkins`, {
        ...checkinData,
        goals_progress: goalsWithProgress
      });

      setShowSuccess(true);
      setTimeout(() => {
        onComplete?.();
        onStatsUpdate?.();
      }, 2000);

    } catch (error) {
      console.error('Failed to submit check-in:', error);
      alert('Failed to submit check-in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEnergyLabel = (level) => {
    if (level <= 2) return 'EXHAUSTED';
    if (level <= 4) return 'LOW';
    if (level <= 6) return 'MODERATE';
    if (level <= 8) return 'HIGH';
    return 'MAXIMUM POWER';
  };

  const getEnergyColor = (level) => {
    if (level <= 2) return 'text-red-400';
    if (level <= 4) return 'text-orange-400';
    if (level <= 6) return 'text-yellow-400';
    if (level <= 8) return 'text-electric-blue';
    return 'text-neon-green';
  };

  if (showSuccess) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-12 border border-neon-green/50 text-center">
        <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-power">
          <Calendar className="w-10 h-10 text-white" />
        </div>
        <h2 className="heading-bold text-white mb-4">CHECK-IN COMPLETE!</h2>
        <p className="text-neon-green text-lg font-bold mb-2">🔥 AMAZING WORK TODAY! 🔥</p>
        <p className="text-gray-300">
          Your progress has been recorded. Keep this momentum going - you're building something incredible!
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 border border-gray-700">
        <div className="text-center mb-8">
          <h2 className="heading-bold text-white mb-4 flex items-center justify-center">
            <Calendar className="w-7 h-7 mr-3 text-neon-green" />
            DAILY CHECK-IN
          </h2>
          <p className="text-gray-300 text-lg">
            Time to reflect on your progress and set the energy for tomorrow! 🚀
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Energy Level */}
          <div className="bg-gray-800/50 rounded-2xl p-6">
            <h3 className="text-white font-bold text-lg mb-4 flex items-center">
              <Battery className="w-5 h-5 mr-2 text-electric-blue" />
              ENERGY LEVEL
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Rate your energy today:</span>
                <span className={`font-bold text-xl ${getEnergyColor(checkinData.energy_level)}`}>
                  {checkinData.energy_level}/10 - {getEnergyLabel(checkinData.energy_level)}
                </span>
              </div>
              
              <input
                type="range"
                min="1"
                max="10"
                value={checkinData.energy_level}
                onChange={(e) => setCheckinData(prev => ({ ...prev, energy_level: Number(e.target.value) }))}
                className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #ef4444 0%, #f59e0b 50%, #10b981 100%)`
                }}
              />
              
              <div className="flex justify-between text-xs text-gray-400">
                <span>Exhausted</span>
                <span>Moderate</span>
                <span>Maximum</span>
              </div>
            </div>
          </div>

          {/* Mood Selection */}
          <div className="bg-gray-800/50 rounded-2xl p-6">
            <h3 className="text-white font-bold text-lg mb-4 flex items-center">
              <Heart className="w-5 h-5 mr-2 text-pink-400" />
              HOW ARE YOU FEELING?
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {moods.map((mood) => (
                <button
                  key={mood.value}
                  type="button"
                  onClick={() => setCheckinData(prev => ({ ...prev, mood: mood.value }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    checkinData.mood === mood.value
                      ? 'border-electric-blue bg-electric-blue/20'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="text-3xl mb-2">{mood.emoji}</div>
                  <div className={`text-sm font-bold ${mood.color}`}>{mood.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Goals Progress */}
          {goals.length > 0 && (
            <div className="bg-gray-800/50 rounded-2xl p-6">
              <h3 className="text-white font-bold text-lg mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-neon-green" />
                GOAL PROGRESS TODAY
              </h3>
              
              <div className="space-y-4">
                {goals.map((goal) => {
                  const progress = checkinData.goals_progress.find(p => p.goal_id === goal.id);
                  
                  return (
                    <div key={goal.id} className="bg-gray-700/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-white font-medium">{goal.title}</h4>
                        <span className="text-gray-400 text-sm">
                          {goal.current_value}/{goal.target_value} {goal.unit}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-300 text-sm mb-2">
                            Progress made today ({goal.unit}):
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={progress?.progress_made || 0}
                            onChange={(e) => updateGoalProgress(goal.id, 'progress_made', Number(e.target.value))}
                            className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-electric-blue outline-none"
                            placeholder="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-300 text-sm mb-2">
                            Notes (optional):
                          </label>
                          <input
                            type="text"
                            value={progress?.notes || ''}
                            onChange={(e) => updateGoalProgress(goal.id, 'notes', e.target.value)}
                            className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-electric-blue outline-none"
                            placeholder="How did it go?"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reflection Notes */}
          <div className="bg-gray-800/50 rounded-2xl p-6">
            <h3 className="text-white font-bold text-lg mb-4">
              DAILY REFLECTION
            </h3>
            
            <textarea
              value={checkinData.notes}
              onChange={(e) => setCheckinData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="What went well today? What could be improved? Any insights or challenges?"
              className="w-full bg-gray-700 text-white px-4 py-4 rounded-xl border border-gray-600 focus:border-electric-blue outline-none resize-none"
              rows="4"
            />
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-power px-12 py-4 text-lg flex items-center space-x-3 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>SUBMITTING...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>COMPLETE CHECK-IN</span>
                </>
              )}
            </button>
            
            <p className="text-gray-400 text-sm mt-4">
              Great job taking time to reflect! This builds self-awareness and momentum. 🌟
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DailyCheckin;